import { useCallback, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { useWindowResize } from '@/hooks/useWindowSize';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

/**
 * ### Description
 *
 * This hook watches for the `useLayoutEffect` changes of a particular `HTMLDivElement` and returns its width.
 *
 * ### Returns
 *
 * - The `ref` to be used on the `HTMLDivElement` you want to watch
 * - The `width` state of the `HTMLDivElement` appended with the `ref`
 */
export const useResponsiveWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [resizeStatus, setResizsStatus] = useState<'idle' | 'resizing'>('idle');

  const debouncedWindowResizeIdle = debounce(
    () => setResizsStatus('idle'),
    300
  );

  const handleResize = useCallback(() => {
    if (resizeStatus === 'idle') {
      setResizsStatus('resizing');
    }
    debouncedWindowResizeIdle();
  }, [resizeStatus, debouncedWindowResizeIdle]);

  useWindowResize(handleResize);

  useIsomorphicLayoutEffect(() => {
    if (resizeStatus === 'resizing') return;
    const { current } = ref;
    if (current) {
      const nextWidth = current.getBoundingClientRect().width;
      if (nextWidth > 0 && nextWidth !== width) {
        setWidth(nextWidth);
      }
    }
  }, [width, resizeStatus]);

  return {
    ref,
    width,
  };
};
